import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/dashboard/Layout';
import MovementModal from '../components/MovementModal';
import MaintenanceModal from '../components/MaintenanceModal';
import { assetsAPI } from '../services/api';
import { ArrowLeft, MapPin, Calendar, DollarSign, Package, History, Wrench, Plus } from 'lucide-react';

const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState({ maintenance_history: [], movement_history: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  useEffect(() => {
    loadAssetData();
  }, [id]);

  const loadAssetData = async () => {
    try {
      const [assetRes, historyRes] = await Promise.all([
        assetsAPI.getById(id),
        assetsAPI.getHistory(id)
      ]);
      setAsset(assetRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to load asset:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ready: 'bg-green-500/10 text-green-600 border-green-500/30',
      rented: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      on_duty: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
      maintenance: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
      service: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
      repair: 'bg-red-500/10 text-red-600 border-red-500/30',
      retired: 'bg-gray-500/10 text-gray-600 border-gray-500/30'
    };
    return colors[status] || colors.ready;
  };

  const getStatusLabel = (status) => {
    const labels = {
      ready: '✅ Ready',
      rented: '🔄 Rented',
      on_duty: '💼 On Duty',
      maintenance: '🔧 Maintenance',
      service: '⚠️ Service',
      repair: '🛠️ Repair',
      retired: '🚫 Retired'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-muted-foreground">Loading asset details...</p>
        </div>
      </Layout>
    );
  }

  if (!asset) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Asset not found</p>
            <button
              onClick={() => navigate('/assets')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Back to Assets
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/assets')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              data-testid="back-to-assets-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {asset.brand} {asset.model}
              </h1>
              <p className="text-muted-foreground mono">{asset.barcode}</p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(asset.status)}`}>
            {getStatusLabel(asset.status)}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b border-border">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('movement')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'movement'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Movement History
          </button>
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'maintenance'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Maintenance & Service
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="glass-card p-6 rounded-xl border border-border/40">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2" />
                Informasi Asset
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Kategori:</span>
                  <span className="text-foreground font-medium capitalize">{asset.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Serial Number:</span>
                  <span className="text-foreground font-mono">{asset.serial_number}</span>
                </div>
                {asset.processor && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processor:</span>
                    <span className="text-foreground">{asset.processor}</span>
                  </div>
                )}
                {asset.ram && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">RAM:</span>
                    <span className="text-foreground">{asset.ram}</span>
                  </div>
                )}
                {asset.storage && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Storage:</span>
                    <span className="text-foreground">{asset.storage}</span>
                  </div>
                )}
                {asset.screen_size && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Screen Size:</span>
                    <span className="text-foreground">{asset.screen_size}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Info */}
            <div className="glass-card p-6 rounded-xl border border-border/40">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                Informasi Keuangan
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Harga Beli:</span>
                  <span className="text-foreground font-medium">{formatCurrency(asset.purchase_price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nilai Saat Ini:</span>
                  <span className="text-foreground font-medium">{formatCurrency(asset.current_value)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Depresiasi:</span>
                  <span className="text-orange-600 font-medium">
                    {formatCurrency(asset.purchase_price - asset.current_value)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tanggal Pembelian:</span>
                  <span className="text-foreground">{formatDate(asset.purchase_date)}</span>
                </div>
              </div>
            </div>

            {/* Location & Maintenance */}
            <div className="glass-card p-6 rounded-xl border border-border/40">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Lokasi & Status
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Lokasi Saat Ini:</span>
                  <span className="text-foreground font-medium">{asset.current_location || 'Gudang Utama'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(asset.status)}`}>
                    {getStatusLabel(asset.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Maintenance Schedule */}
            <div className="glass-card p-6 rounded-xl border border-border/40">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Jadwal Maintenance
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Maintenance Terakhir:</span>
                  <span className="text-foreground">{formatDate(asset.last_maintenance_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Maintenance Berikutnya:</span>
                  <span className="text-foreground">{formatDate(asset.next_maintenance_due)}</span>
                </div>
              </div>
            </div>

            {/* Condition Notes */}
            {asset.condition_notes && (
              <div className="glass-card p-6 rounded-xl border border-border/40 md:col-span-2">
                <h3 className="text-lg font-semibold text-foreground mb-4">Catatan Kondisi</h3>
                <p className="text-muted-foreground">{asset.condition_notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'movement' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-foreground">Riwayat Perpindahan Asset</h3>
              <button
                onClick={() => setShowMovementModal(true)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Catat Perpindahan</span>
              </button>
            </div>

            {history.movement_history && history.movement_history.length > 0 ? (
              <div className="space-y-3">
                {history.movement_history.map((movement, index) => (
                  <div key={index} className="glass-card p-6 rounded-xl border border-border/40">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-medium">
                            {movement.movement_type?.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-sm text-muted-foreground">{formatDate(movement.date)}</span>
                        </div>
                        <div className="space-y-1">
                          <p className="text-foreground">
                            <span className="text-muted-foreground">Dari:</span> {movement.from_location || '-'}
                            <span className="mx-2">→</span>
                            <span className="text-muted-foreground">Ke:</span> {movement.to_location || '-'}
                          </p>
                          {movement.notes && (
                            <p className="text-sm text-muted-foreground">{movement.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="glass-card p-12 rounded-xl border border-border/40 text-center">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Belum ada riwayat perpindahan</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-foreground">Riwayat Maintenance & Service</h3>
              <button
                onClick={() => setShowMaintenanceModal(true)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Catat Maintenance</span>
              </button>
            </div>

            {history.maintenance_history && history.maintenance_history.length > 0 ? (
              <div className="space-y-3">
                {history.maintenance_history.map((maintenance, index) => (
                  <div key={index} className="glass-card p-6 rounded-xl border border-border/40">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                            maintenance.type === 'service' 
                              ? 'bg-orange-500/10 text-orange-600' 
                              : 'bg-blue-500/10 text-blue-600'
                          }`}>
                            {maintenance.type === 'service' ? '⚠️ SERVICE' : '🔧 MAINTENANCE'}
                          </span>
                          <span className="text-sm text-muted-foreground">{formatDate(maintenance.date)}</span>
                        </div>
                        <p className="text-foreground mb-2">{maintenance.description}</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {maintenance.cost > 0 && (
                            <div>
                              <span className="text-muted-foreground">Biaya:</span>
                              <span className="text-foreground ml-2 font-medium">{formatCurrency(maintenance.cost)}</span>
                            </div>
                          )}
                          {maintenance.technician && (
                            <div>
                              <span className="text-muted-foreground">Teknisi:</span>
                              <span className="text-foreground ml-2">{maintenance.technician}</span>
                            </div>
                          )}
                        </div>
                        {maintenance.parts_replaced && maintenance.parts_replaced.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm text-muted-foreground">Part yang diganti:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {maintenance.parts_replaced.map((part, idx) => (
                                <span key={idx} className="px-2 py-1 bg-muted rounded text-xs text-foreground">
                                  {part}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="glass-card p-12 rounded-xl border border-border/40 text-center">
                <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Belum ada riwayat maintenance atau service</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Movement Modal */}
      {showMovementModal && (
        <MovementModal
          assetId={id}
          onClose={() => setShowMovementModal(false)}
          onSuccess={loadAssetData}
        />
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && (
        <MaintenanceModal
          assetId={id}
          onClose={() => setShowMaintenanceModal(false)}
          onSuccess={loadAssetData}
        />
      )}
    </Layout>
  );
};

export default AssetDetailPage;
